<template>
    <div>
        <div v-if="isPDF">
            <ossPdf :documentId="document.id" />
        </div>
        <div
            v-else
            style="
                position: relative;
                width: 100%;
                height: 0;
                padding: 0;
                padding-bottom: 141.4516129032258%;
                margin-bottom: 50px;
            "
        >
            <svg
                id="svg-document-validation"
                v-if="document"
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                :viewBox="`0 0 ${imageWidth} ${imageHeight}`"
                style="position: absolute; width: 100%; left: 0; top: 0; background: #fff"
                :key="showOCR"
            >
                <image
                    id="svg-image-document-validation"
                    :xlink:href="document.signedUrl"
                    x="0"
                    y="0"
                    :height="`${imageHeight}px`"
                    :width="`${imageWidth}px`"
                />
                <template v-if="!showOCR">
                    <template v-for="(variant, index) in svgVariants">
                        <template
                            v-for="(coord, index2) in variant.coordinates"
                            :key="`coord-${document.id}-${index}-${index2}`"
                        >
                            <polygon
                                :ref="`variants-${index}-${index2}`"
                                :points="getPoints(coord)"
                                :style="
                                    variant.active
                                        ? 'fill:rgba(250,250,250,0.5);stroke:#F9B12A;stroke-width:1'
                                        : 'fill:rgba(250,250,250,0.5);stroke:gray;stroke-width:1;cursor:pointer'
                                "
                                @click.stop="
                                    variant.active
                                        ? ''
                                        : showCorrespondingField(variant, index, index2)
                                "
                                :class="variant.focus ? 'highlighted' : ''"
                            />
                        </template>
                    </template>
                </template>
                <template v-else-if="showOCR && ocr && ocr.sentences && ocr.sentences.length">
                    <template v-for="(sentence, sIndex) in ocr.sentences">
                        <template
                            v-for="(token, tIndex) in sentence.tokens"
                            :key="`sentence-${sIndex}-token-${tIndex}`"
                        >
                            <polygon
                                :ref="`sentence-${sIndex}-token-${tIndex}`"
                                :points="getTokenPoints(token)"
                                :style="getTokenStyle(token)"
                                @mouseover="token.high = true"
                                @mouseout="token.high = false"
                                @click.stop="onClickToken(token)"
                            />
                        </template>
                    </template>
                </template>
            </svg>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { Emit } from "vue-facing-decorator";

@Component
export class DocumentFileViewer extends Vue {
    @Prop({ required: true, type: Object }) document!: any;
    @Prop() svgVariants: Array<any>;
    @Prop({ default: false }) showOCR: boolean;
    @Prop() ocr: any;

    get file() {
        return this.document ? this.document.workingCopy || this.document.original : null;
    }

    get signedUrl(): string {
        return this.document?.signedUrl || "";
    }

    get imageWidth() {
        return this.file ? this.file.width || 20.0 : 20.0;
    }

    get imageHeight() {
        return this.file ? this.file.height || 20.0 : 20.0;
    }

    get svgScale() {
        if (this.imageWidth && this.document?.analysis?.pdf?.pages?.length) {
            const firstPage = this.document.analysis.pdf.pages[0];
            if (firstPage?.width) {
                return this.imageWidth / firstPage.width;
            }
        }
        return 1;
    }

    get isPDF() {
        return this.file && this.file.type === "PDF";
    }

    getPoints(vertices: any) {
        if (vertices && vertices.length) {
            return vertices.reduce(
                (acc: string, el: { x: number; y: number }) =>
                    `${acc} ${el.x * this.svgScale},${el.y * this.svgScale} `,
                ""
            );
        }
        return "";
    }

    getTokenPoints(token: any) {
        if (
            token &&
            token.boundingPoly &&
            token.boundingPoly.vertices &&
            token.boundingPoly.vertices.length
        ) {
            return token.boundingPoly.vertices.reduce(
                (acc: string, el: { x: number; y: number }) =>
                    `${acc} ${el.x * this.svgScale},${el.y * this.svgScale} `,
                ""
            );
        }
        return "";
    }

    getTokenStyle(token) {
        let style = "cursor:pointer;";
        if (token.selected) {
            style += "fill:rgba(249,177,42,0.5);stroke:#F9B12A;";
            style += token.high ? "stroke-width:3" : "stroke-width:2";
        } else {
            style += "fill:rgba(250,250,250,0.5);stroke:gray;";
            style += token.high ? "stroke-width:3" : "stroke-width:1";
        }
        return style;
    }

    @Emit("showCorrespondingField")
    showCorrespondingField(variant: any, currentIndex: number, copyIndex: number) {
        return { variant, currentIndex, copyIndex };
    }

    @Emit("token-click")
    onClickToken(token) {
        return token;
    }
}

export default toNative(DocumentFileViewer);
</script>

<style lang="scss" scoped>
polygon.highlighted {
    fill: rgba(249, 177, 42, 0.5) !important;
}
</style>
